import type { Pagination } from '@grafikr/shopify-typescript/type/liquid/Pagination';
import { Component } from '@grafikr/components';

import fetchText from 'utils/fetch-text';
import { NonNullish } from 'utils/nullish-check';
import setUrlParams from 'utils/set-url-params';

type Part = {
  page: number;
  size: number;
  url: URL;
  isLoaded: boolean;
};

const tabbableSelectors = [
  'input:not([inert])',
  'select:not([inert])',
  'textarea:not([inert])',
  'a[href]:not([inert])',
  'button:not([inert])',
  '[tabindex]:not(slot):not([inert])',
  'audio[controls]:not([inert])',
  'video[controls]:not([inert])',
  '[contenteditable]:not([contenteditable="false"]):not([inert])',
  'details>summary:first-of-type:not([inert])',
  'details:not([inert])',
];

export default Component((node, { app, onMounted }) => {
  const data: Pagination = JSON.parse(NonNullish(node.querySelector('[data-data]')?.textContent));
  const grid = NonNullish(document.querySelector(NonNullish(node.dataset.gridSelector)));

  const count = NonNullish(node.querySelector('[data-count]'));
  const progress = NonNullish(node.querySelector<HTMLElement>('[data-progress]'));
  const previousButton = node.querySelector<HTMLButtonElement>('[data-previous]');
  const nextButton = node.querySelector<HTMLButtonElement>('[data-next]');

  const parts = new Map<number, Part>();

  for (let i = 0; i < data.pages; i += 1) {
    const page = i + 1;
    const size =
      (i === data.pages - 1 ? data.items % data.page_size : data.page_size) || data.page_size;
    const url = new URL(window.location.toString());
    url.searchParams.append('page', (i + 1).toString());
    url.searchParams.append('view', 'items');

    parts.set(page, {
      page,
      size,
      url,
      isLoaded: page === data.current_page,
    });
  }

  let previousPart = parts.get(data.current_page - 1);
  let nextPart = parts.get(data.current_page + 1);

  const updateProgress = () => {
    let currentOffset = 0;
    let currentProgress = 0;
    let hasFoundLoadedPart = false;

    parts.forEach((part) => {
      if (part.isLoaded) {
        currentProgress += part.size;
        hasFoundLoadedPart = true;
      }

      if (!hasFoundLoadedPart) {
        currentOffset += part.size;
      }
    });

    count.textContent = currentProgress.toString();
    progress.style.setProperty('--progress', `${(currentProgress / data.items) * 100}%`);
    progress.style.setProperty('--offset', `${(currentOffset / data.items) * 100}%`);
  };

  const updatePageParam = (part: Part) => {
    const params = setUrlParams(new URLSearchParams(window.location.search), {
      page: part.page.toString(),
    });

    window.history.replaceState(null, '', `?${params.toString()}`);
  };

  const loadPart = async (part: Part) => {
    if (part.isLoaded) {
      throw new Error('Pagination part is already loaded or loading');
    }

    part.isLoaded = true;
    updateProgress();

    return fetchText(part.url);
  };

  const insertItems = (position: 'previous' | 'next', text: string, autoFocus: boolean) => {
    grid.role = 'alert';

    const content = document.createElement('div');
    content.insertAdjacentHTML(position === 'previous' ? 'afterbegin' : 'beforeend', text);
    const children = Array.from(content.children);

    if (position === 'previous') {
      grid.prepend(...children);

      if (autoFocus) {
        children.at(-1)?.querySelector<HTMLElement>(tabbableSelectors.join(', '))?.focus();
      }
    } else {
      grid.append(...children);

      if (autoFocus) {
        children.at(0)?.querySelector<HTMLElement>(tabbableSelectors.join(', '))?.focus();
      }
    }

    app.mount();
  };

  const loadPrevious = async (event?: Event) => {
    if (!previousPart) {
      return;
    }

    if (event) {
      updatePageParam(previousPart);
    }

    insertItems('previous', await loadPart(previousPart), event !== undefined);

    previousPart = parts.get(previousPart.page - 1);

    if (!previousPart && previousButton) {
      previousButton.remove();
    }
  };

  const loadNext = async (event?: Event) => {
    if (!nextPart) {
      return;
    }

    if (event) {
      updatePageParam(nextPart);
    }

    insertItems('next', await loadPart(nextPart), event !== undefined);

    nextPart = parts.get(nextPart.page + 1);

    if (!nextPart && nextButton) {
      nextButton.remove();
    }
  };

  onMounted(async () => {
    previousButton?.addEventListener('click', loadPrevious);
    nextButton?.addEventListener('click', loadNext);

    if (node.hasAttribute('data-autoload-previous')) {
      while (previousPart) {
        await loadPrevious();
      }
    }
  });
});
